import { ConfigurableCard } from "../../components/ConfigurableCard";
import { DocumentItem } from "./components/DocumentItem";
import { Bank, BriefCase01, File05 } from "untitledui-js-base";
import { useTemplateContext } from "../../provider";
import { VerificationNames } from "shared/types";
import { TIMEFRAME } from "./constants";
import { DocumentTypeVerification } from "types/landlord/verificationTemplate";
import { trasformTimeframeToOptions } from "./utils";

export const DocumentVerificationSlice = () => {
  const { readOnly, template, partnerType, setTemplate, verifiedIncome } =
    useTemplateContext();

  if (!template) return null;

  const verificationSchemas = template.verification_schemas[partnerType];

  const verifiedIncomeVerificationSchema =
    verificationSchemas[VerificationNames.VerifiedIncomeVerification];

  const isVerificationEnabled =
    Boolean(verificationSchemas[VerificationNames.IncomeVerification]) &&
    Boolean(verifiedIncomeVerificationSchema);

  const askCustomer =
    Boolean(verifiedIncomeVerificationSchema) &&
    "allow_document" in (verifiedIncomeVerificationSchema || {})
      ? verifiedIncomeVerificationSchema?.["allow_document"]
      : verifiedIncome["allow_document"];

  const isDocumentEnabled = isVerificationEnabled && askCustomer && !readOnly;

  const updateTemplate = (updatedSchema: any) => {
    setTemplate({
      ...template,
      verification_schemas: {
        ...template.verification_schemas,
        [partnerType]: updatedSchema
      }
    });
  };

  const toggleDocumentEnabled = () => {
    updateTemplate({
      ...template.verification_schemas[partnerType],
      [VerificationNames.VerifiedIncomeVerification]: {
        ...verifiedIncomeVerificationSchema,
        allow_document: !askCustomer
      }
    });
  };

  const onSelectDocumentTimeframe = (
    docType: DocumentTypeVerification,
    value: number
  ) => {
    updateTemplate({
      ...template.verification_schemas[partnerType],
      [VerificationNames.VerifiedIncomeVerification]: {
        ...verifiedIncomeVerificationSchema,
        document_timeframe: {
          ...verifiedIncomeVerificationSchema?.["document_timeframe"],
          [docType]: {
            ...verifiedIncomeVerificationSchema?.["document_timeframe"]?.[
              docType
            ],
            value: value,
            unit: TIMEFRAME[docType].unit
          }
        }
      }
    });
  };

  const getDocumentTimeframe = (docType: DocumentTypeVerification) =>
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    verifiedIncomeVerificationSchema?.["document_timeframe"]?.[docType]
      ?.value ?? TIMEFRAME[docType].max_value;

  const documentItems = [
    {
      id: DocumentTypeVerification.PayStubs,
      title: "Paystubs",
      Icon: BriefCase01,
      options: trasformTimeframeToOptions(TIMEFRAME.pay_stubs),
      defaultValue: getDocumentTimeframe(DocumentTypeVerification.PayStubs)
    },
    {
      id: DocumentTypeVerification.BankStatements,
      title: "Bank statements",
      Icon: Bank,
      options: trasformTimeframeToOptions(TIMEFRAME.bank_statements),
      defaultValue: getDocumentTimeframe(
        DocumentTypeVerification.BankStatements
      )
    },
    {
      id: DocumentTypeVerification.W2,
      title: "W-2",
      Icon: File05,
      options: trasformTimeframeToOptions(TIMEFRAME.w2),
      defaultValue: getDocumentTimeframe(DocumentTypeVerification.W2)
    }
    // in the future
    // {
    //   id: DocumentTypeVerification.TaxReturns,
    //   title: "Tax returns",
    //   Icon: PieChart01,
    //   options: trasformTimeframeToOptions(TIMEFRAME.tax_returns),
    //   defaultValue: getDocumentTimeframe(DocumentTypeVerification.TaxReturns)
    // }
  ];

  return (
    <ConfigurableCard
      title="Document verification"
      rightElement={
        <img
          className="h-[22px]"
          src="/assets/ocrolus_logo.svg"
          alt="Ocrolus logo"
        />
      }
      switchProps={{
        checked: Boolean(askCustomer),
        onChange: toggleDocumentEnabled,
        disabled: !isVerificationEnabled || readOnly,
        label: "Allow verification via document upload"
      }}
    >
      <div className="mt-4 pt-4 border-t border-alpha-40">
        <p className="text-sm text-content-400 font-medium">
          Set the requested timeframe for the documents
        </p>
        <div className="space-y-2 mt-4">
          {documentItems.map(({ id, title, Icon, options, defaultValue }) => (
            <DocumentItem
              key={id}
              id={id}
              title={title}
              Icon={Icon}
              options={options}
              disabled={!isDocumentEnabled}
              defaultValue={defaultValue}
              onSelect={(value: number) =>
                onSelectDocumentTimeframe(id as DocumentTypeVerification, value)
              }
            />
          ))}
        </div>
      </div>
    </ConfigurableCard>
  );
};
